/* Design score UI (contract section 5): a badge like "8.2 / 10 · Great" and
 * a panel with the four categories and every issue in plain words, each with
 * a "Show me" that selects the visual. Scores come from the server's
 * deterministic scorer (backend/services/design_score.py) via /validate, so
 * the designer, the gallery and the AI authoring API all agree. */
import { useQuery } from "@tanstack/react-query";
import { Crosshair, Gauge, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { designApi, type Area, type DesignIssue, type DesignScore } from "../api/v21";
import { Button } from "../ds";
import "./designScore.css";

export function scoreColor(score: number | null | undefined): string {
  if (score === null || score === undefined) return "var(--text-faint)";
  if (score >= 9) return "#1f9d63";
  if (score >= 8) return "#2e9d68";
  if (score >= 6) return "#1a86c9";
  if (score >= 4) return "#d9911a";
  return "#c94343";
}

export function gradeFor(score: number): string {
  return score >= 9 ? "Excellent" : score >= 8 ? "Great" : score >= 6 ? "Good" : score >= 4 ? "Fair" : "Needs work";
}

const SEV_COLOR: Record<DesignIssue["severity"], string> = { high: "var(--danger)", medium: "var(--warning)", low: "var(--info)" };
const SEV_LABEL: Record<DesignIssue["severity"], string> = { high: "Fix first", medium: "Worth fixing", low: "Polish" };

export function DesignScoreBadge({
  score,
  grade,
  loading,
  onClick,
  size = "md",
  title,
}: {
  score: number | null | undefined;
  grade?: string;
  loading?: boolean;
  onClick?: () => void;
  size?: "sm" | "md";
  title?: string;
}) {
  const has = typeof score === "number";
  const label = has ? `${score!.toFixed(1)} / 10` : loading ? "Scoring…" : "No score yet";
  const g = grade ?? (has ? gradeFor(score!) : "");
  const Tag = onClick ? "button" : "span";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      className={`dsc-badge${size === "sm" ? " dsc-badge--sm" : ""}${onClick ? "" : " is-static"}`}
      onClick={onClick ? (e: React.MouseEvent) => { e.stopPropagation(); onClick(); } : undefined}
      title={title ?? (has ? `Design score ${score!.toFixed(1)} out of 10 — ${g}. How good the layout looks on a TV.` : "Design score")}
      aria-label={has ? `Design score ${score!.toFixed(1)} out of 10, ${g}` : "Design score"}
    >
      <span className="dsc-dot" style={{ background: scoreColor(score), opacity: loading ? 0.55 : 1 }} aria-hidden><Gauge size={size === "sm" ? 11 : 14} /></span>
      <span>{label}</span>
      {g && size !== "sm" ? <span className="dsc-grade">· {g}</span> : g ? <span className="dsc-grade">{g}</span> : null}
    </Tag>
  );
}

function Ring({ score }: { score: number }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <svg className="dsc-ring" width={68} height={68} viewBox="0 0 68 68" aria-hidden>
      <circle cx={34} cy={34} r={r} fill="none" stroke="var(--surface-2)" strokeWidth={7} />
      <circle cx={34} cy={34} r={r} fill="none" stroke={scoreColor(score)} strokeWidth={7} strokeLinecap="round" strokeDasharray={`${(score / 10) * c} ${c}`} transform="rotate(-90 34 34)" />
      <text x={34} y={39} textAnchor="middle" fontSize={17} fontWeight={750} fill="var(--text)" fontFamily="inherit">{score.toFixed(1)}</text>
    </svg>
  );
}

export function DesignScorePanel({
  design,
  onSelectWidget,
  pageNames,
  currentPageId,
  onClose,
}: {
  design: DesignScore;
  onSelectWidget?: (pageId: string, widgetId: string | null) => void;
  pageNames?: Record<string, string>;
  currentPageId?: string;
  onClose?: () => void;
}) {
  const [filter, setFilter] = useState<"all" | "page">("all");
  const names = pageNames ?? Object.fromEntries((design.pages ?? []).map((p) => [p.pageId, p.name]));
  const multiPage = (design.pages?.length ?? 0) > 1;
  const groups = groupIssues(design.issues.filter((i) => filter === "all" || !currentPageId || i.pageId === currentPageId || i.pageId === null));
  const total = design.issueCount ?? design.issues.length;
  return (
    <div>
      <div className="dsc-head">
        <Ring score={design.score} />
        <div className="ds-grow" style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{design.grade} <span className="ds-muted" style={{ fontWeight: 500, fontSize: 13 }}>design score</span></div>
          <div className="ds-small ds-muted" style={{ marginTop: 2 }}>{design.summary}</div>
        </div>
        {onClose ? <Button size="sm" variant="ghost" icon={X} onClick={onClose} aria-label="Close" /> : null}
      </div>
      <div className="dsc-cats">
        {design.categories.map((c) => (
          <div key={c.key} className="dsc-cat">
            <span>{c.label}</span>
            <span className="dsc-bar"><span style={{ width: `${c.score * 10}%`, background: scoreColor(c.score) }} /></span>
            <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 650 }}>{c.score.toFixed(1)}</span>
          </div>
        ))}
      </div>
      <div className="ds-row" style={{ justifyContent: "space-between", marginBottom: 2 }}>
        <strong style={{ fontSize: 13 }}>{groups.length} tip{groups.length === 1 ? "" : "s"}{filter === "all" && total > groups.length ? <span className="ds-muted" style={{ fontWeight: 500 }}> · {total} places</span> : null}</strong>
        {multiPage && currentPageId ? (
          <div className="ds-seg" role="group" style={{ padding: 2 }}>
            <button type="button" className="ds-seg__btn" aria-pressed={filter === "all"} onClick={() => setFilter("all")}>All pages</button>
            <button type="button" className="ds-seg__btn" aria-pressed={filter === "page"} onClick={() => setFilter("page")}>This page</button>
          </div>
        ) : null}
      </div>
      {groups.length === 0 ? (
        <div className="ds-small ds-muted" style={{ padding: "12px 0", display: "flex", gap: 8, alignItems: "center" }}><Sparkles size={15} /> Nothing to fix here — nice work.</div>
      ) : (
        groups.slice(0, 40).map((g, n) => {
          const i = g.first;
          const here = g.places.find((p) => p.pageId === currentPageId) ?? g.places[0];
          const pagesNamed = [...new Set(g.places.map((p) => (p.pageId ? names[p.pageId] : null)).filter(Boolean))] as string[];
          return (
            <div key={n} className="dsc-issue">
              <span className="dsc-sev" style={{ background: SEV_COLOR[i.severity] }} title={SEV_LABEL[i.severity]} />
              <div style={{ minWidth: 0 }}>
                <div className="dsc-issue__msg">{i.message}</div>
                <div className="dsc-issue__fix">{i.fix}</div>
                <div className="dsc-issue__where" title={pagesNamed.join(", ")}>
                  {SEV_LABEL[i.severity]}
                  {multiPage && pagesNamed.length === 1 ? ` · ${pagesNamed[0]}` : ""}
                  {multiPage && pagesNamed.length > 1 ? ` · on ${pagesNamed.length} pages (${pagesNamed.slice(0, 3).join(", ")}${pagesNamed.length > 3 ? "…" : ""})` : ""}
                </div>
              </div>
              {onSelectWidget && here?.pageId ? (
                <Button size="sm" variant="ghost" icon={Crosshair} onClick={() => onSelectWidget(here.pageId!, here.widgetId)} title={here.widgetId ? "Select this visual" : "Go to this page"}>Show me</Button>
              ) : <span />}
            </div>
          );
        })
      )}
    </div>
  );
}

/** The same tip repeated on many pages (e.g. one misalignment copied to 36
 * vessel pages) is shown once, with the pages it applies to. */
export function groupIssues(issues: DesignIssue[]): Array<{ first: DesignIssue; places: Array<{ pageId: string | null; widgetId: string | null }> }> {
  const map = new Map<string, { first: DesignIssue; places: Array<{ pageId: string | null; widgetId: string | null }> }>();
  for (const i of issues) {
    const key = `${i.code ?? ""}|${i.message}|${i.fix}`;
    const g = map.get(key);
    if (g) g.places.push({ pageId: i.pageId, widgetId: i.widgetId });
    else map.set(key, { first: i, places: [{ pageId: i.pageId, widgetId: i.widgetId }] });
  }
  return [...map.values()];
}

/** Debounced live score of an (unsaved) definition via /validate. */
export function useLiveDesignScore(area: Area, reportId: string, definition: unknown, delay = 1400) {
  const [debounced, setDebounced] = useState<string | null>(null);
  useEffect(() => {
    if (!definition) return;
    const t = window.setTimeout(() => setDebounced(JSON.stringify(definition)), delay);
    return () => window.clearTimeout(t);
  }, [definition, delay]);
  const q = useQuery({
    queryKey: ["design-score", area, reportId, debounced],
    queryFn: () => designApi.validate(area, reportId, JSON.parse(debounced!)),
    enabled: !!debounced && !!reportId,
    placeholderData: (prev) => prev,
    staleTime: Infinity,
    retry: false,
  });
  return { design: q.data?.design, loading: q.isFetching || (!!definition && debounced === null), error: q.error };
}

/** Badge + pop-over panel for the designer toolbar. */
export function DesignScoreToolbarItem({
  mode,
  reportId,
  definition,
  currentPageId,
  onSelectWidget,
}: {
  mode: Area;
  reportId: string;
  definition: unknown;
  currentPageId?: string;
  onSelectWidget: (pageId: string, widgetId: string | null) => void;
}) {
  const { design, loading } = useLiveDesignScore(mode, reportId, definition);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("mousedown", down);
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("mousedown", down); window.removeEventListener("keydown", key); };
  }, [open]);
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <DesignScoreBadge score={design?.score} grade={design?.grade} loading={loading} onClick={() => setOpen((v) => !v)} />
      {open && design ? (
        <div className="dsc-pop" role="dialog" aria-label="Design score">
          <DesignScorePanel design={design} currentPageId={currentPageId} onClose={() => setOpen(false)} onSelectWidget={(p, w) => onSelectWidget(p, w)} />
        </div>
      ) : null}
    </div>
  );
}

/** Workspace side card: the saved draft's score, the top tips, and the full
 * panel in a dialog. */
export function ReportDesignCard({
  area,
  reportId,
  revision,
  onSelectWidget,
  onOpenDesigner,
}: {
  area: Area;
  reportId: string;
  revision?: number;
  onSelectWidget?: (pageId: string, widgetId: string | null) => void;
  onOpenDesigner?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ["design-score-saved", area, reportId, revision], queryFn: () => designApi.validate(area, reportId), enabled: !!reportId && revision !== undefined, staleTime: 60_000, retry: false });
  const design = q.data?.design;
  return (
    <div className="ds-card">
      <header className="ds-card__header">
        <div style={{ minWidth: 0 }}>
          <h3 className="ds-card__title">Design score</h3>
          <p className="ds-card__subtitle">How good the layout looks on a TV</p>
        </div>
        <DesignScoreBadge size="sm" score={design?.score} grade={design?.grade} loading={q.isFetching} onClick={design ? () => setOpen(true) : undefined} />
      </header>
      <div className="ds-card__body">
        {q.isLoading ? (
          <div className="ds-small ds-muted">Scoring the draft…</div>
        ) : !design ? (
          <div className="ds-small ds-muted">{q.isError ? "Couldn't score the draft right now." : "No score yet."}</div>
        ) : (
          <>
            <div className="ds-small ds-muted" style={{ marginBottom: 8 }}>{design.summary}</div>
            {groupIssues(design.issues).slice(0, 3).map((g, n) => (
              <div key={n} className="ds-small" style={{ display: "flex", gap: 8, padding: "5px 0", alignItems: "flex-start" }}>
                <span className="dsc-sev" style={{ background: SEV_COLOR[g.first.severity], marginTop: 5, flex: "none" }} />
                <span>{g.first.message}{g.places.length > 1 ? <span className="ds-faint"> · {g.places.length} places</span> : null}</span>
              </div>
            ))}
            <div className="ds-row" style={{ gap: 8, marginTop: 8 }}>
              <Button size="sm" onClick={() => setOpen(true)}>See all tips</Button>
              {onOpenDesigner ? <Button size="sm" variant="ghost" onClick={onOpenDesigner}>Fix in designer</Button> : null}
            </div>
          </>
        )}
      </div>
      {open && design ? (
        <div className="ds-overlay" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className="ds-modal" role="dialog" aria-modal="true" aria-label="Design score" style={{ padding: 20, maxWidth: 520 }}>
            <DesignScorePanel design={design} onClose={() => setOpen(false)} onSelectWidget={onSelectWidget ? (p, w) => { onSelectWidget(p, w); setOpen(false); } : undefined} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
